import { CheckCircle, Clock, Send, XCircle, AlertTriangle, UserCheck } from "lucide-react";
import type { TaxDocumentStatus, ReceptorActionStatus } from "../types";
import { RECEPTOR_STATUS_LABELS } from "../types";

interface TimelineStep {
  status: TaxDocumentStatus;
  label: string;
  timestamp?: string | null;
}

interface StatusTimelineProps {
  currentStatus: TaxDocumentStatus;
  createdAt: string;
  submittedAt?: string | null;
  acceptedAt?: string | null;
  statusDetail?: string | null;
  authorityStatusCode?: string | null;
  receptorStatus?: ReceptorActionStatus | null;
  receptorActionAt?: string | null;
}

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleString("es-CL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getStepIcon(step: TimelineStep, currentStatus: TaxDocumentStatus) {
  const isCompleted = step.timestamp != null;
  const isCurrent = step.status === currentStatus;
  const isRejected = currentStatus === "rejected" && step.status === "accepted";

  if (isRejected) {
    return <XCircle className="h-5 w-5 text-red-500" />;
  }
  if (isCompleted) {
    return <CheckCircle className="h-5 w-5 text-green-500" />;
  }
  if (isCurrent) {
    return <Clock className="h-5 w-5 text-yellow-500" />;
  }
  return <div className="h-5 w-5 rounded-full border-2 border-muted" />;
}

function getReceptorStatusIcon(status: ReceptorActionStatus | null | undefined) {
  if (!status || status === "pending") {
    return <Clock className="h-5 w-5 text-yellow-500" />;
  }
  if (status === "accepted" || status === "goods_received" || status === "auto_accepted") {
    return <CheckCircle className="h-5 w-5 text-green-500" />;
  }
  // Claimed statuses
  return <AlertTriangle className="h-5 w-5 text-red-500" />;
}

export function StatusTimeline({
  currentStatus,
  createdAt,
  submittedAt,
  acceptedAt,
  statusDetail,
  authorityStatusCode,
  receptorStatus,
  receptorActionAt,
}: StatusTimelineProps) {
  const steps: TimelineStep[] = [
    {
      status: "pending",
      label: "Generado",
      timestamp: createdAt,
    },
    {
      status: "submitted",
      label: "Enviado a SII",
      timestamp: submittedAt,
    },
    {
      status: "accepted",
      label: currentStatus === "rejected" ? "Rechazado" : "Aceptado por SII",
      timestamp: currentStatus === "rejected" || currentStatus === "accepted" ? acceptedAt : null,
    },
  ];

  // Show receptor status section only after SII acceptance
  const showReceptorStatus = currentStatus === "accepted";

  return (
    <div className="space-y-6">
      {/* SII Status Section */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <Send className="h-4 w-4" />
          Estado SII
          {authorityStatusCode && (
            <span className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
              {authorityStatusCode}
            </span>
          )}
        </h3>

        <div className="space-y-4">
          {steps.map((step, index) => {
            const isLast = index === steps.length - 1;

            return (
              <div key={step.status} className="flex gap-4">
                <div className="flex flex-col items-center">
                  {getStepIcon(step, currentStatus)}
                  {!isLast && (
                    <div className="w-0.5 h-8 bg-muted mt-1" />
                  )}
                </div>
                <div className="flex-1 pb-4">
                  <div className="font-medium text-sm">{step.label}</div>
                  {step.timestamp && (
                    <div className="text-xs text-muted-foreground">
                      {formatDateTime(step.timestamp)}
                    </div>
                  )}
                  {step.status === currentStatus && statusDetail && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {statusDetail}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Receptor Status Section */}
      {showReceptorStatus && (
        <div className="space-y-4 pt-4 border-t">
          <h3 className="text-sm font-medium flex items-center gap-2">
            <UserCheck className="h-4 w-4" />
            Estado Receptor
          </h3>

          <div className="flex gap-4">
            <div className="flex flex-col items-center">
              {getReceptorStatusIcon(receptorStatus)}
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm">
                {receptorStatus
                  ? RECEPTOR_STATUS_LABELS[receptorStatus]
                  : "Pendiente de acción"}
              </div>
              {receptorActionAt && (
                <div className="text-xs text-muted-foreground">
                  {formatDateTime(receptorActionAt)}
                </div>
              )}
              {receptorStatus === "pending" && (
                <div className="text-xs text-muted-foreground mt-1">
                  El receptor tiene 8 días para aceptar o reclamar
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
